import React, { useEffect, useState, useCallback } from "react"
import { TextField, Button, Typography, Snackbar, Alert, Select, MenuItem, FormControl, InputLabel } from "@mui/material"
import Box from "@mui/material/Box"
import { styled } from "@mui/system"
import { useNavigate } from "react-router-dom"
import { useSignIn } from "react-auth-kit"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import Visibility from "@mui/icons-material/Visibility"
import VisibilityOff from "@mui/icons-material/VisibilityOff"
import api from "../utils/api"
import axios from "axios"

// Define a type for the event objects
interface Event {
  id: string;
  name: string;
  archiveStatus: boolean;
}

const CenteredBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "16px",
  marginTop: "32px",
})

export const VoterLoginForm: React.FC<{ notification?: string }> = ({
  notification = "",
}) => {
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [eventId, setEventId] = useState("")
  const navigate = useNavigate()
  const signIn = useSignIn()
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "info" | "warning"
  >("success")
  const [showPassword, setShowPassword] = useState(false)
  const [activeEvents, setActiveEvents] = useState<Event[]>([])

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false)
    setSnackbarMessage(null)
  }, [])

  useEffect(() => {
    if (notification) {
      setSnackbarMessage(notification)
      setSnackbarSeverity("success")
      setSnackbarOpen(true)
    }
  }, [notification])

  useEffect(() => {
    const fetchActiveEvents = async () => {
      console.log("Fetching active events...");
      try {
        const response = await api.get("/admin/events/active")
        console.log("Fetched active events:", response.data);
        setActiveEvents(response.data || []) // Ensure it's always an array
      } catch (error) {
        console.error("Error fetching active events:", error)
        setActiveEvents([]) // Set to empty array on error
      }
    }
    fetchActiveEvents()
  }, [])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    try {
      const response = await api.post("/voter/login", {
        username,
        password,
        eventId,
      })

      const data = response.data

      signIn({
        token: data.token,
        expiresIn: 3600,
        tokenType: "Bearer",
        authState: {
          id: data.id,
          name: data.name,
          eventId: data.eventId,
          username: data.username,
          type: "voter",
        },
      })
      navigate("/voter/dashboard", {
        state: { notification: "Login successful!", eventId: eventId },
      })
    } catch (error) {
      // Extracting error message from response, if available
      const errorMessage =
        error.response?.data?.message ||
        "Invalid Credentials 🙁. Please try again."
      setSnackbarMessage(errorMessage)
      setSnackbarSeverity("error")
      setSnackbarOpen(true)
    }
  }

  return (
    <CenteredBox>
      <Typography variant="h5">Voter Login</Typography>
      <form onSubmit={handleSubmit}>
        <TextField
          label="Username"
          variant="outlined"
          value={username}
          onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
          fullWidth
          margin="normal"
          required
        />



        <FormControl fullWidth margin="normal">
          <InputLabel required id="event-select-label">
            Event
          </InputLabel>
            <Select
              label="Event"
              labelId="event-select-label"
              id="event-select"
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              required
              >
               {Array.isArray(activeEvents) ? activeEvents.map((event: Event) => (
            <MenuItem key={event.id} value={event.id}>
              {event.name}
            </MenuItem>
                )) : <MenuItem value="">No active events available</MenuItem>}
              </Select>
        </FormControl>

        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          variant="outlined"
          value={password}
          onChange={(e) => setPassword(e.target.value.replace(/\s/g, ""))}
          fullWidth
          required
          margin="normal"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button type="submit" variant="contained" color="primary">
          Login
        </Button>
      </form>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={snackbarSeverity === "error" ? 6000 : 3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </CenteredBox>
  )
}
